import { z } from 'zod'
import { email, uuid } from './comum.js'

/**
 * A VISÃO de um portal externo (D-131): Cliente ou Parceiro, escolhida ao criar e imutável.
 * Substitui o papel do vínculo (`'Cliente/Anunciante'`/`'Parceiro'`, DS-29); na tela ela se lê
 * "Portal do Cliente" e "Portal do Parceiro" (DS-519, GLOSSARIO_UI).
 */
export const visaoDoPortal = z.enum(['Cliente', 'Parceiro'])
export type VisaoDoPortal = z.infer<typeof visaoDoPortal>

export const estadoDoUsuarioPortal = z.enum(['convidado', 'ativo', 'revogado'])
export const estadoDoPortalExterno = z.enum(['ativo', 'arquivado'])
export const estadoDoMembroDoPortal = z.enum(['ativo', 'revogado'])

/** Uma empresa do cadastro dentro de um portal. O portal nunca cria empresa: só a escolhe. */
export const empresaDoPortalExterno = z.object({
  id: uuid,
  nome: z.string(),
  documento: z.string().nullable(),
})
export type EmpresaDoPortalExterno = z.infer<typeof empresaDoPortalExterno>

/** Um Usuário do portal que entra num portal (o "membro"; o termo não aparece na tela). */
export const membroDoPortalExterno = z.object({
  id: uuid,
  usuarioPortalId: uuid,
  nome: z.string(),
  /** O contato da pessoa, quando ela tem um: o nome abre a ficha dele (D-136, DS-538). */
  contatoId: uuid.nullable(),
  email,
  /** O estado da PESSOA: convidada ainda não definiu a senha. */
  estadoDoUsuario: estadoDoUsuarioPortal,
  /** O estado do acesso a ESTE portal. */
  estado: estadoDoMembroDoPortal,
  revogadoEm: z.string().nullable(),
  ultimoAcessoEm: z.string().nullable(),
})
export type MembroDoPortalExterno = z.infer<typeof membroDoPortalExterno>

export const portalExterno = z.object({
  id: uuid,
  nome: z.string(),
  visao: visaoDoPortal,
  estado: estadoDoPortalExterno,
  arquivadoEm: z.string().nullable(),
  empresas: z.array(empresaDoPortalExterno),
  membros: z.array(membroDoPortalExterno),
})
export type PortalExterno = z.infer<typeof portalExterno>

/**
 * Empresa que a tela SUGERE para um portal — do mesmo grupo econômico ou relacionada (D-47) a
 * uma empresa que ele já mostra. Nunca entra sozinha: só com o clique de quem administra.
 */
export const sugestaoDeEmpresaDoPortal = z.object({
  id: uuid,
  nome: z.string(),
  motivo: z.literal('relacionada'),
})
export type SugestaoDeEmpresaDoPortal = z.infer<typeof sugestaoDeEmpresaDoPortal>

export const entradaDePortalExterno = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do portal.'),
  visao: visaoDoPortal,
  empresaIds: z.array(uuid).min(1, 'Escolha ao menos uma empresa.'),
})
export type EntradaDePortalExterno = z.infer<typeof entradaDePortalExterno>

export const entradaDeEdicaoDePortalExterno = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do portal.'),
})
export type EntradaDeEdicaoDePortalExterno = z.infer<typeof entradaDeEdicaoDePortalExterno>

export const entradaDeEmpresaDoPortal = z.object({ empresaId: uuid })
export type EntradaDeEmpresaDoPortal = z.infer<typeof entradaDeEmpresaDoPortal>

/**
 * Convidar um Usuário do portal para um portal. Pessoa nova ou ainda convidada recebe o
 * convite de senha; quem JÁ está ativo recebe só o aviso de acesso, sem token (DS-519(c)).
 * O Contato, quando informado, precisa pertencer a uma das empresas do portal (DS-527).
 */
export const entradaDeMembroDoPortal = z.object({
  email,
  contatoId: uuid.nullish(),
})
export type EntradaDeMembroDoPortal = z.infer<typeof entradaDeMembroDoPortal>

export const respostaDeMembroDoPortal = z.object({
  portal: portalExterno,
  /** `null` quando a pessoa JÁ estava ativa: aviso de acesso, sem link de senha. */
  linkDeConvite: z.url().nullable(),
  expiraEm: z.string().nullable(),
})
export type RespostaDeMembroDoPortal = z.infer<typeof respostaDeMembroDoPortal>

/** Na lista de Usuários do portal: os portais em que a pessoa entra. */
export const acessoDoUsuarioAoPortal = z.object({
  membroId: uuid,
  portalId: uuid,
  portalNome: z.string(),
  visao: visaoDoPortal,
  estado: estadoDoMembroDoPortal,
  portalArquivado: z.boolean(),
  revogadoEm: z.string().nullable(),
})
export type AcessoDoUsuarioAoPortal = z.infer<typeof acessoDoUsuarioAoPortal>

export const usuarioDoPortal = z.object({
  id: uuid,
  email,
  nome: z.string(),
  contatoId: uuid.nullable(),
  estado: estadoDoUsuarioPortal,
  ultimoAcessoEm: z.string().nullable(),
  portais: z.array(acessoDoUsuarioAoPortal),
})
export type UsuarioDoPortal = z.infer<typeof usuarioDoPortal>
